/**
 * Comprehensive Error Handling Utilities
 * Provides centralized error handling for payment and account creation failures
 * 
 * Requirements: 5.1, 5.2, 5.5
 */

import { 
  trackCategorizedError, 
  trackErrorRecovery,
  getUserFriendlyErrorMessage,
  getRecoveryActions,
  ERROR_CATEGORIES,
  ERROR_SEVERITY,
  RECOVERY_STRATEGIES
} from '@/utils/error-categorization';
import { trackEvent } from '@/utils/analytics';
import logger from '@/utils/logger';

/**
 * Enhanced error handler for checkout and account creation flows
 * Provides comprehensive error processing with recovery options
 */
export class CheckoutErrorHandler {
  constructor(context = 'checkout') {
    this.context = context;
    this.errorHistory = [];
    this.recoveryAttempts = new Map();
  }

  /**
   * Process and handle an error with comprehensive analysis
   * @param {Error} error - The error object
   * @param {string} stage - The stage where error occurred (payment, account_creation, etc.)
   * @param {Object} metadata - Additional error context
   * @returns {Object} Processed error with recovery options
   */
  handleError(error, stage, metadata = {}) {
    const errorId = this.generateErrorId();
    const timestamp = new Date().toISOString();
    
    // Categorize the error
    const categorizedError = trackCategorizedError(error, `${this.context}_${stage}`, {
      error_id: errorId,
      stage,
      ...metadata
    });

    // Get user-friendly message and recovery actions
    const userMessage = getUserFriendlyErrorMessage(categorizedError.category);
    const recoveryActions = getRecoveryActions(categorizedError.category);

    // Create comprehensive error response
    const processedError = {
      id: errorId,
      timestamp,
      stage,
      category: categorizedError.category,
      severity: categorizedError.severity,
      recoveryStrategy: categorizedError.recoveryStrategy,
      userMessage,
      recoveryActions,
      originalError: {
        message: error.message,
        code: error.code || error.type,
        stack: error.stack?.substring(0, 500)
      },
      metadata: {
        context: this.context,
        stage,
        ...metadata
      },
      canRetry: this.canRetry(categorizedError.category, stage),
      retryCount: this.getRetryCount(errorId),
      supportReference: this.generateSupportReference(errorId)
    };

    // Store error in history
    this.errorHistory.push(processedError);

    // Log error for monitoring
    logger.error('Checkout error processed', {
      errorId,
      category: categorizedError.category,
      severity: categorizedError.severity,
      stage,
      context: this.context
    });

    // Send critical error notifications
    if (categorizedError.severity === ERROR_SEVERITY.CRITICAL) {
      this.sendCriticalErrorNotification(processedError);
    }

    return processedError;
  }

  /**
   * Handle payment-specific errors with enhanced context
   * @param {Error} error - Payment error
   * @param {Object} paymentContext - Payment-specific context
   * @returns {Object} Processed payment error
   */
  handlePaymentError(error, paymentContext = {}) {
    const enhancedContext = {
      payment_method: paymentContext.paymentMethod || 'stripe',
      pricing_tier: paymentContext.pricingTier,
      session_id: paymentContext.sessionId,
      customer_id: paymentContext.customerId,
      amount: paymentContext.amount,
      currency: paymentContext.currency || 'AUD',
      ...paymentContext
    };

    return this.handleError(error, 'payment', enhancedContext);
  }

  /**
   * Handle account creation errors with enhanced context
   * @param {Error} error - Account creation error
   * @param {Object} accountContext - Account-specific context
   * @returns {Object} Processed account creation error
   */
  handleAccountCreationError(error, accountContext = {}) {
    const enhancedContext = {
      email: accountContext.email,
      phone_provided: !!accountContext.phoneNumber,
      stripeCustomerId: accountContext.stripeCustomerId,
      subscription_id: accountContext.subscriptionId,
      twilio_setup_attempted: accountContext.twilioSetupAttempted,
      ...accountContext
    };

    return this.handleError(error, 'account_creation', enhancedContext);
  }

  /**
   * Handle Twilio integration errors
   * @param {Error} error - Twilio error
   * @param {Object} twilioContext - Twilio-specific context
   * @returns {Object} Processed Twilio error
   */
  handleTwilioError(error, twilioContext = {}) {
    const enhancedContext = {
      phone_number: twilioContext.phoneNumber,
      verification_sid: twilioContext.verificationSid,
      twilio_service: twilioContext.service || 'sms',
      ...twilioContext
    };

    return this.handleError(error, 'twilio_integration', enhancedContext);
  }

  /**
   * Handle authentication errors
   * @param {Error} error - Authentication error
   * @param {Object} authContext - Authentication-specific context
   * @returns {Object} Processed authentication error
   */
  handleAuthError(error, authContext = {}) {
    const enhancedContext = {
      auth_method: authContext.authMethod || 'jwt',
      user_id: authContext.userId,
      session_id: authContext.sessionId,
      ...authContext
    };

    return this.handleError(error, 'authentication', enhancedContext);
  }

  /**
   * Track error recovery attempt
   * @param {string} errorId - Original error ID
   * @param {string} recoveryAction - Recovery action taken
   * @param {Object} recoveryContext - Recovery context
   * @returns {string} Recovery attempt ID
   */
  trackRecoveryAttempt(errorId, recoveryAction, recoveryContext = {}) {
    const recoveryId = this.generateRecoveryId();
    const attemptNumber = this.incrementRetryCount(errorId);
    
    const recoveryData = {
      recovery_id: recoveryId,
      original_error_id: errorId,
      recovery_action: recoveryAction,
      attempt_number: attemptNumber,
      timestamp: new Date().toISOString(),
      context: this.context,
      ...recoveryContext
    };

    // Track recovery attempt
    trackEvent('error_recovery_attempt', recoveryData);
    
    logger.log('Error recovery attempt tracked', recoveryData);

    return recoveryId;
  }

  /**
   * Track successful error recovery
   * @param {string} errorId - Original error ID
   * @param {string} recoveryAction - Recovery action that succeeded
   * @param {Object} successContext - Success context
   */
  trackRecoverySuccess(errorId, recoveryAction, successContext = {}) {
    const originalError = this.errorHistory.find(e => e.id === errorId);
    
    if (originalError) {
      trackErrorRecovery(
        originalError.category,
        recoveryAction,
        true,
        {
          recovery_time: Date.now() - new Date(originalError.timestamp).getTime(),
          attempt_number: this.getRetryCount(errorId),
          original_error_id: errorId,
          context: this.context,
          ...successContext
        }
      );
    }

    // Track success event
    trackEvent('error_recovery_success', {
      original_error_id: errorId,
      recovery_action: recoveryAction,
      context: this.context,
      ...successContext
    });

    logger.log('Error recovery successful', {
      errorId,
      recoveryAction,
      context: this.context
    });
  }

  /**
   * Check if an error can be retried
   * @param {string} errorCategory - Error category
   * @param {string} stage - Error stage
   * @returns {boolean} Whether retry is allowed
   */
  canRetry(errorCategory, stage) {
    const maxRetries = this.getMaxRetries(errorCategory, stage);
    const currentRetries = this.recoveryAttempts.get(errorCategory) || 0;
    
    return currentRetries < maxRetries;
  }

  /**
   * Get maximum retry attempts for error category and stage
   * @param {string} errorCategory - Error category
   * @param {string} stage - Error stage
   * @returns {number} Maximum retry attempts
   */
  getMaxRetries(errorCategory, stage) {
    const retryLimits = {
      // Payment errors
      [ERROR_CATEGORIES.PAYMENT_NETWORK_ERROR]: 3,
      [ERROR_CATEGORIES.PAYMENT_PROCESSING_ERROR]: 2,
      [ERROR_CATEGORIES.PAYMENT_CARD_DECLINED]: 1,
      
      // Network errors
      [ERROR_CATEGORIES.NETWORK_TIMEOUT]: 3,
      [ERROR_CATEGORIES.NETWORK_CONNECTION_LOST]: 3,
      [ERROR_CATEGORIES.NETWORK_SERVER_ERROR]: 2,
      
      // Account creation errors
      [ERROR_CATEGORIES.ACCOUNT_TWILIO_FAILED]: 2,
      [ERROR_CATEGORIES.ACCOUNT_EMAIL_SERVICE_FAILED]: 2,
      [ERROR_CATEGORIES.ACCOUNT_DATABASE_ERROR]: 1,
      
      // Authentication errors
      [ERROR_CATEGORIES.AUTH_TOKEN_GENERATION_FAILED]: 2,
      [ERROR_CATEGORIES.AUTH_SESSION_CREATION_FAILED]: 2,
    };

    return retryLimits[errorCategory] || 1;
  }

  /**
   * Get current retry count for error
   * @param {string} errorId - Error ID
   * @returns {number} Current retry count
   */
  getRetryCount(errorId) {
    return this.recoveryAttempts.get(errorId) || 0;
  }

  /**
   * Increment retry count for error
   * @param {string} errorId - Error ID
   * @returns {number} New retry count
   */
  incrementRetryCount(errorId) {
    const currentCount = this.getRetryCount(errorId);
    const newCount = currentCount + 1;
    this.recoveryAttempts.set(errorId, newCount);
    return newCount;
  }

  /**
   * Send critical error notification to administrators
   * @param {Object} processedError - Processed error object
   */
  async sendCriticalErrorNotification(processedError) {
    try {
      const notificationData = {
        error_id: processedError.id,
        category: processedError.category,
        severity: processedError.severity,
        stage: processedError.stage,
        context: this.context,
        user_message: processedError.userMessage.message,
        support_reference: processedError.supportReference,
        timestamp: processedError.timestamp,
        metadata: processedError.metadata
      };

      // Track critical error for monitoring
      trackEvent('critical_error_notification', notificationData);

      // Log for admin monitoring systems
      logger.error('CRITICAL ERROR - Admin notification required', notificationData);

      // TODO: Implement actual notification system (email, Slack, etc.)
      console.error('CRITICAL ERROR NOTIFICATION:', notificationData);

    } catch (notificationError) {
      logger.error('Failed to send critical error notification', {
        original_error_id: processedError.id,
        notification_error: notificationError.message
      });
    }
  }

  /**
   * Generate unique error ID
   * @returns {string} Unique error ID
   */
  generateErrorId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `ERR-${timestamp}-${random}`.toUpperCase();
  }

  /**
   * Generate unique recovery ID
   * @returns {string} Unique recovery ID
   */
  generateRecoveryId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6);
    return `REC-${timestamp}-${random}`.toUpperCase();
  }

  /**
   * Generate support reference for customer service
   * @param {string} errorId - Error ID
   * @returns {string} Support reference
   */
  generateSupportReference(errorId) {
    const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const shortId = errorId.split('-')[1].substring(-6);
    return `SUP-${date}-${shortId}`;
  }

  /**
   * Get error history for debugging
   * @returns {Array} Error history
   */
  getErrorHistory() {
    return [...this.errorHistory];
  }

  /**
   * Clear error history (useful for testing)
   */
  clearErrorHistory() {
    this.errorHistory = [];
    this.recoveryAttempts.clear();
  }
}

/**
 * Global error handler instance for checkout flows
 */
export const checkoutErrorHandler = new CheckoutErrorHandler('checkout');

/**
 * Utility function to handle payment errors consistently
 * @param {Error} error - Payment error
 * @param {Object} context - Payment context
 * @returns {Object} Processed error with recovery options
 */
export const handlePaymentError = (error, context = {}) => {
  return checkoutErrorHandler.handlePaymentError(error, context);
};

/**
 * Utility function to handle account creation errors consistently
 * @param {Error} error - Account creation error
 * @param {Object} context - Account context
 * @returns {Object} Processed error with recovery options
 */
export const handleAccountCreationError = (error, context = {}) => {
  return checkoutErrorHandler.handleAccountCreationError(error, context);
};

/**
 * Utility function to handle Twilio errors consistently
 * @param {Error} error - Twilio error
 * @param {Object} context - Twilio context
 * @returns {Object} Processed error with recovery options
 */
export const handleTwilioError = (error, context = {}) => {
  return checkoutErrorHandler.handleTwilioError(error, context);
};

/**
 * Utility function to handle authentication errors consistently
 * @param {Error} error - Authentication error
 * @param {Object} context - Authentication context
 * @returns {Object} Processed error with recovery options
 */
export const handleAuthError = (error, context = {}) => {
  return checkoutErrorHandler.handleAuthError(error, context);
};

/**
 * Enhanced error boundary for React components
 * Provides comprehensive error handling for UI components
 */
export class CheckoutErrorBoundary {
  constructor(componentName = 'Unknown') {
    this.componentName = componentName;
    this.errorHandler = new CheckoutErrorHandler(`component_${componentName.toLowerCase()}`);
  }

  /**
   * Handle component errors with recovery options
   * @param {Error} error - Component error
   * @param {Object} errorInfo - React error info
   * @returns {Object} Processed error for UI display
   */
  handleComponentError(error, errorInfo = {}) {
    const context = {
      component_name: this.componentName,
      component_stack: errorInfo.componentStack?.substring(0, 500),
      error_boundary: true,
      ...errorInfo
    };

    return this.errorHandler.handleError(error, 'component_render', context);
  }

  /**
   * Get fallback UI data for error display
   * @param {Object} processedError - Processed error object
   * @returns {Object} Fallback UI configuration
   */
  getFallbackUI(processedError) {
    return {
      title: processedError.userMessage.title,
      message: processedError.userMessage.message,
      actions: processedError.recoveryActions,
      supportReference: processedError.supportReference,
      canRetry: processedError.canRetry,
      severity: processedError.severity
    };
  }
}

/**
 * Admin notification system for critical failures
 * Handles sending notifications to administrators for manual intervention
 */
export class AdminNotificationSystem {
  constructor() {
    this.notificationQueue = [];
    this.isProcessing = false;
  }

  /**
   * Queue critical error for admin notification
   * @param {Object} errorData - Error data for notification
   */
  async queueCriticalError(errorData) {
    const notification = {
      id: this.generateNotificationId(),
      type: 'critical_error',
      timestamp: new Date().toISOString(),
      priority: this.getPriority(errorData.severity),
      data: errorData
    };

    this.notificationQueue.push(notification);
    
    // Process queue if not already processing
    if (!this.isProcessing) {
      await this.processNotificationQueue();
    }
  }

  /**
   * Process notification queue
   */
  async processNotificationQueue() {
    if (this.isProcessing || this.notificationQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      while (this.notificationQueue.length > 0) {
        const notification = this.notificationQueue.shift();
        await this.sendNotification(notification);
      }
    } catch (error) {
      logger.error('Error processing notification queue', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Send individual notification
   * @param {Object} notification - Notification to send
   */
  async sendNotification(notification) {
    try {
      // Log notification for monitoring systems
      logger.error('ADMIN NOTIFICATION', notification);

      // Track notification for analytics
      trackEvent('admin_notification_sent', {
        notification_id: notification.id,
        type: notification.type,
        priority: notification.priority,
        error_category: notification.data.category,
        timestamp: notification.timestamp
      });

      // TODO: Implement actual notification channels
      // - Email to administrators
      // - Slack webhook
      // - SMS for critical errors
      // - Dashboard alerts

      console.error('ADMIN NOTIFICATION SENT:', {
        id: notification.id,
        type: notification.type,
        priority: notification.priority,
        summary: notification.data.userMessage?.title || 'Critical Error',
        supportReference: notification.data.supportReference
      });

    } catch (error) {
      logger.error('Failed to send admin notification', {
        notification_id: notification.id,
        error: error.message
      });
    }
  }

  /**
   * Get notification priority based on error severity
   * @param {string} severity - Error severity
   * @returns {string} Notification priority
   */
  getPriority(severity) {
    const priorityMap = {
      [ERROR_SEVERITY.CRITICAL]: 'urgent',
      [ERROR_SEVERITY.HIGH]: 'high',
      [ERROR_SEVERITY.MEDIUM]: 'medium',
      [ERROR_SEVERITY.LOW]: 'low'
    };

    return priorityMap[severity] || 'medium';
  }

  /**
   * Generate unique notification ID
   * @returns {string} Notification ID
   */
  generateNotificationId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6);
    return `NOT-${timestamp}-${random}`.toUpperCase();
  }
}

/**
 * Global admin notification system instance
 */
export const adminNotificationSystem = new AdminNotificationSystem();